import { Area, AreaChart, ResponsiveContainer, Tooltip, CartesianGrid, XAxis } from 'recharts';
import { Button, PlantIcon } from '@practium/ui';
import styles from './RevenueCard.module.css';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { RevenueTooltip } from '../ToolTips/RevenueTooltip';
import { useNavigate } from 'react-router-dom';

interface RevenueCardProps {
	mainValue: string;
	trendValue: string;
	trendDirection: 'up' | 'down';
	chartData: Array<{ day: string; value: number }>;
}

export const RevenueCard = ({ mainValue, trendValue, trendDirection, chartData }: RevenueCardProps) => {
	const navigate = useNavigate();
	const isEmpty = !chartData || chartData.length === 0;

	return (
		<div className={styles.card}>
			<div className={styles.header}>
				<h3 className={styles.title}>Faturamento</h3>
				<Button
					onClick={() => {
						navigate('/faturamento');
					}}
					variant="secondary"
					className={styles.button}
				>
					Ver Mais
				</Button>
			</div>
			<div className={styles.revenueSection}>
				<p className={styles.subtitle}>Analise todas as informações sobre o faturamento</p>
				<div className={styles.valueContainer}>
					<span className={styles.value}>{mainValue}</span>
					<div className={`${styles.trend} ${trendDirection === 'up' ? styles.trendUp : styles.trendDown}`}>
						<span>
							{trendDirection === 'up' ? (
								<ArrowUpward className={styles.trendIcon} />
							) : (
								<ArrowDownward className={styles.trendIcon} />
							)}
						</span>
						<span className={styles.trendValue}>{trendValue}</span>
						<span className={styles.trendText}> vs mês passado</span>
					</div>
				</div>
			</div>
			<div className={styles.chartContainer}>
				{isEmpty ? (
					<div className={styles.emptyState}>
						<PlantIcon className={styles.emptyIcon} />
						<span className={styles.emptyMessage}>Nenhum dado disponível</span>
					</div>
				) : (
					<ResponsiveContainer width="100%" height="100%">
						<AreaChart data={chartData} margin={{ top: 0, right: 5, left: 5, bottom: 0 }}>
							<defs>
								<linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
									<stop offset="5%" stopColor="var(--primary-dark)" stopOpacity={0.7} />
									<stop offset="35%" stopColor="var(--gradient-chart-start)" stopOpacity={0.5} />
									<stop offset="85%" stopColor="var(--gradient-chart-end)" stopOpacity={0.1} />
									<stop offset="100%" stopColor="var(--gradient-chart-end)" stopOpacity={0} />
								</linearGradient>
							</defs>
							<CartesianGrid
								strokeDasharray="0"
								stroke="var(--neutral-100)"
								horizontal={false}
								vertical={true}
							/>
							<XAxis
								dataKey="day"
								hide={false}
								axisLine={false}
								tickLine={false}
								tick={{ fill: 'var(--text-secondary)', fontSize: '0.75rem' }}
								interval="preserveStartEnd"
							/>
							<Tooltip content={<RevenueTooltip />} cursor={false} />
							<Area
								type="monotone"
								dataKey="value"
								stroke="var(--primary)"
								strokeWidth={2}
								fillOpacity={1}
								fill="url(#colorRevenue)"
								activeDot={{ r: 4, fill: 'var(--primary)' }}
							/>
						</AreaChart>
					</ResponsiveContainer>
				)}
			</div>
		</div>
	);
};
